import type { ReportTypeId } from '../../config/reportTypes';
import { REPORT_PROMPT_VERSIONS } from '../../config/reportPromptVersions';
import type { SiteRagReport } from '../../types/siteRag';

/**
 * Known historical prompt versions require refresh. Unversioned legacy reports
 * retain the pre-existing SED, intent and authority checks; their prompt cannot
 * be retrospectively verified and is labelled explicitly instead of guessed.
 */
export function reportPromptContractCurrency(report: Pick<SiteRagReport, 'promptVersion' | 'generationTelemetry'>, type: ReportTypeId): {
  current: boolean; status: 'current' | 'refresh-needed' | 'legacy-unversioned'; expectedVersion: string; actualVersion?: string;
} {
  const expectedVersion = REPORT_PROMPT_VERSIONS[type];
  const actualVersion = report.promptVersion ?? report.generationTelemetry?.promptVersion;
  if (!actualVersion) return { current: true, status: 'legacy-unversioned', expectedVersion };
  return { current: actualVersion === expectedVersion, status: actualVersion === expectedVersion ? 'current' : 'refresh-needed', expectedVersion, actualVersion };
}

/**
 * Incremental carry-forward reuses a parent's prose verbatim. That is only
 * sound under the SAME prompt contract: prose written under an older (or
 * unrecorded) prompt, spliced into a report stamped with the current version,
 * would ship old-contract narrative under a new-contract label. Treat any
 * difference, including an unversioned parent, as a change that forces a full
 * regeneration (over-regenerating is acceptable; under-regenerating is not).
 */
export function reportPromptContractChanged(report: Pick<SiteRagReport, 'promptVersion' | 'generationTelemetry'>, type: ReportTypeId): boolean {
  return reportPromptContractCurrency(report, type).status !== 'current';
}
